var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import InputMask from 'react-input-mask';
import { Input } from '../Input';
import { keys } from '../../utilities/keyCodes';
import { compareObj, isInvalidDate } from './utils/utils';
import { formatDate, isFocusInsideDatePicker, parseEntry, } from './utils/maskUtils';
import { expandMaskValue, findMaskFieldBoundariesAtPosition, } from './utils/expandMaskUtils';
export const defaultFormat = 'MM/DD/YYYY';
export const defaultMaskChar = '_';
export const InputDateMask = (_a) => {
    var { alwaysShowMask, className, dateFormat = defaultFormat, disabled, error, maskChar = defaultMaskChar, maxDate: max, minDate: min, onChange, onBlur, placeholder, required, shortLabel, size, value, tabIndex, inputRef, calendarRef, input } = _a, rest = __rest(_a, ["alwaysShowMask", "className", "dateFormat", "disabled", "error", "maskChar", "maxDate", "minDate", "onChange", "onBlur", "placeholder", "required", "shortLabel", "size", "value", "tabIndex", "inputRef", "calendarRef", "input"]);
    const [entry, setEntry] = useState(''); // Input's text entry, e.g. mask value
    const [rangeError, setRangeError] = useState(false);
    const [requiredError, setRequiredError] = useState(false);
    const [invalidEntryError, setInvalidEntryError] = useState(false);
    const [expandMaskAtCursor, setExpandMaskAtCursor] = React.useState(false);
    const isChangeFromMask = React.useRef(false);
    const parse = (str = entry) => parseEntry(str, dateFormat, maskChar, min, max, required);
    // Effects
    useEffect(() => {
        if (isInvalidDate(value) || typeof value === 'undefined') {
            if (isChangeFromMask.current) {
                isChangeFromMask.current = false; // In case value came from InputDateMask...
                return; // ...keep user's entry as is
            }
        }
        const nextEntry = formatDate(value, dateFormat);
        setRangeError(parse(nextEntry).isOutRange);
        setRequiredError(false);
        setInvalidEntryError(false);
        setEntry(nextEntry);
    }, [compareObj(value)]); // eslint-disable-line
    useEffect(() => {
        if (!isChangeFromMask.current)
            return; // Prevent double onChange if value changed from outside of InputDateMask
        const { date, isEmpty, isFull, isOutRange, isValid, invalidReason } = parse(); // New entry
        setInvalidEntryError(isFull && !isValid);
        if (!isValid && !isFull && !isEmpty) {
            if ((value && !isInvalidDate(value)) || // `12/12/2023` → `12/12/202_`
                (typeof value === 'undefined' && !isEmpty) // `__/__/____` → `1_/__/____`
            ) {
                isChangeFromMask.current = true;
                onChange(date, { invalidReason });
            }
            return; // Suppress onChange if entry not full
        }
        if (isOutRange || (isFull && !isValid)) {
            isChangeFromMask.current = true;
            setRangeError(true);
            onChange(date, { invalidReason });
            return;
        }
        // Suppress firing onChange for same values
        if (compareObj(value) !== compareObj(date)) {
            isChangeFromMask.current = true;
            onChange(date, { invalidReason }); // Finally
        }
    }, [entry]); // eslint-disable-line
    // Handlers
    const handleMaskKeyDown = (e) => {
        if (e.key === keys.delete) {
            e.preventDefault();
            isChangeFromMask.current = true;
            setRequiredError(required && !parse().isEmpty);
            setEntry(''); // Clear user's entry
        }
        if (e.key === keys.slash) {
            e.preventDefault();
            setExpandMaskAtCursor(true); // Performs `1_/__/____` → `01/__/____`, etc.
        }
    };
    const handleMaskBlur = (e) => {
        if (isFocusInsideDatePicker(inputRef, calendarRef, e))
            return; // Suppress if focus is still inside DatePicker
        const { date, isEmpty, isFull, isOutRange, isValid, invalidReason } = parse();
        setRequiredError(required && !isFull);
        setRangeError(isOutRange);
        setInvalidEntryError(!isEmpty && !isValid);
        onBlur(date, { invalidReason });
        isChangeFromMask.current = false;
    };
    const handleMaskChange = (e) => {
        var _a, _b;
        if (e.type !== 'change')
            return; // Other possible types: focus, blur
        isChangeFromMask.current = true;
        const nextEntry = (_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.value;
        if (nextEntry === entry)
            return;
        (_b = input === null || input === void 0 ? void 0 : input.onChange) === null || _b === void 0 ? void 0 : _b.call(input, e, nextEntry);
        const { isEmpty, isFull, isOutRange, isValid } = parse(nextEntry);
        setRangeError(isOutRange);
        setRequiredError(required && isEmpty);
        setInvalidEntryError(isFull && !isValid);
        setEntry(nextEntry); // Runs effect on state change later to handle onChange
    };
    const handleBeforeMaskChange = (next) => {
        if (expandMaskAtCursor) {
            setExpandMaskAtCursor(false);
            const expanded = expandMaskValue(next.value, next.selection.start);
            if (expanded === entry || parse(expanded).isEmpty)
                return next;
            setEntry(expanded);
            const nextIndex = findMaskFieldBoundariesAtPosition(next.value, next.selection.start)[1];
            next.selection = { start: nextIndex + 1, end: nextIndex + 1 }; // Reposition cursor to next boundary
            next.value = expanded;
        }
        if (entry === '' && parse(next.value).isEmpty)
            next.selection = { start: 0, end: 0 }; // Send cursor to start on clearing mask
        return next;
    };
    /*
     * InputMask mask prop format: https://github.com/sanniassin/react-input-mask
     */
    const mask = dateFormat.replace(/[MmDdYyo]/g, '9');
    const realPlaceholder = placeholder || mask.replace(/9/g, maskChar);
    return (React.createElement(InputMask, Object.assign({}, rest, { mask: mask, disabled: disabled, maskChar: maskChar, onChange: handleMaskChange, onKeyDown: handleMaskKeyDown, onBlur: handleMaskBlur, beforeMaskedValueChange: handleBeforeMaskChange, alwaysShowMask: alwaysShowMask, value: entry }), ((props) => (React.createElement(Input, Object.assign({}, props, { inputRef: inputRef, shortLabel: shortLabel, className: classnames('InputDateMask', className), size: size, fluid: true, error: error ||
            (required && requiredError) ||
            invalidEntryError ||
            ((min || max) && rangeError), disabled: disabled, placeholder: realPlaceholder, tabIndex: tabIndex }))))));
};
//# sourceMappingURL=InputDateMask.js.map